/**
 * Toys Hunter — meccanica del nastro dei brand.
 *
 * Il nastro e un contenitore che scorre davvero (`overflow-x: auto`): dito e
 * rotellina orizzontale funzionano senza codice. Qui dentro ci sono le tre
 * cose che il browser non fa da solo:
 *
 *  1. lo scorrimento automatico, un pixel alla volta, a velocita costante
 *     (px al secondo calcolati dal tempo trascorso, non dal numero di frame);
 *  2. il ciclo infinito: la lista e stampata tre volte e quando la posizione
 *     esce dalla copia centrale la riportiamo indietro di una copia esatta.
 *     Sotto c'e' lo stesso contenuto, quindi il salto non si vede;
 *  3. il trascinamento col mouse, che il browser riserva alle barre di
 *     scorrimento.
 *
 * Pause: passaggio del mouse, focus da tastiera, trascinamento, scheda in
 * secondo piano, nastro fuori schermo, `prefers-reduced-motion`, e una pausa
 * breve dopo ogni gesto dell'utente. Sono contate in un insieme di motivi:
 * si riparte solo quando l'insieme e vuoto.
 *
 * Nessuno scatto alla ripartenza: la posizione non viene mai ricalcolata da
 * zero, si riprende da dove sta il nastro e il primo frame dopo la pausa non
 * produce movimento (serve solo a rimettere in moto l'orologio).
 */
(function () {
  if (customElements.get('th-brand-rail')) return;

  var PAUSA_DOPO_GESTO_MS = 1200;
  var DURATA_SCORRIMENTO_DOLCE_MS = 450;
  var SOGLIA_TRASCINAMENTO_PX = 5;

  class ThBrandRail extends HTMLElement {
    connectedCallback() {
      if (this.pronto) return;

      this.viewport = this.querySelector('[data-th-brands-viewport]');
      this.track = this.querySelector('[data-th-brands-track]');
      if (!this.viewport || !this.track) return;

      this.pronto = true;
      this.abort = new AbortController();
      var opt = { signal: this.abort.signal };
      var optPassivo = { signal: this.abort.signal, passive: true };

      this.durataGiro = parseFloat(this.dataset.speed) || 46;
      this.autoplay = this.dataset.autoplay === '1';
      this.pausaSuHover = this.dataset.pauseHover === '1';

      this.motiviPausa = new Set();
      this.posizione = 0;
      this.ultimoTempo = null;
      this.frame = null;
      this.trascinando = false;
      this.spostamento = 0;
      this.sopprimiClic = false;
      this.dolceFinoA = 0;

      // Si parte dalla copia centrale: c'e' una copia intera di pista sia
      // avanti sia indietro, quindi il trascinamento non trova mai il muro.
      this.vaiAllaCopiaCentrale();

      if (this.pausaSuHover) {
        this.addEventListener('pointerenter', () => this.pausa('hover'), optPassivo);
        this.addEventListener('pointerleave', () => this.riprendi('hover'), optPassivo);
      }

      this.addEventListener('focusin', () => this.pausa('focus'), optPassivo);
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.riprendi('focus');
      }, optPassivo);

      this.viewport.addEventListener('pointerdown', (e) => this.inizioTrascinamento(e), opt);
      this.viewport.addEventListener('click', (e) => this.filtraClic(e), { signal: this.abort.signal, capture: true });

      this.viewport.addEventListener('wheel', () => this.pausaBreve(), optPassivo);
      this.viewport.addEventListener('touchstart', () => this.pausaBreve(), optPassivo);
      this.viewport.addEventListener('keydown', (e) => this.daTastiera(e), opt);
      this.viewport.addEventListener('scroll', () => this.suScorrimento(), optPassivo);

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pausa('nascosto');
        else this.riprendi('nascosto');
      }, optPassivo);

      this.mqMenoMovimento = window.matchMedia('(prefers-reduced-motion: reduce)');
      var applicaMovimento = () => {
        if (this.mqMenoMovimento.matches) this.pausa('meno-movimento');
        else this.riprendi('meno-movimento');
      };
      if (this.mqMenoMovimento.addEventListener) {
        this.mqMenoMovimento.addEventListener('change', applicaMovimento, opt);
      }
      applicaMovimento();

      if ('IntersectionObserver' in window) {
        this.osservatore = new IntersectionObserver((voci) => {
          voci.forEach((v) => {
            if (v.isIntersecting) this.riprendi('fuori-schermo');
            else this.pausa('fuori-schermo');
          });
        });
        this.osservatore.observe(this);
      }

      if ('ResizeObserver' in window) {
        this.osservatoreMisure = new ResizeObserver(() => this.vaiAllaCopiaCentrale());
        this.osservatoreMisure.observe(this.track);
      }

      this.avvia();
    }

    disconnectedCallback() {
      this.fermaFrame();
      if (this.abort) this.abort.abort();
      if (this.osservatore) this.osservatore.disconnect();
      if (this.osservatoreMisure) this.osservatoreMisure.disconnect();
      this.pronto = false;
    }

    /** Larghezza di una copia della lista (il nastro ne stampa tre). */
    larghezzaCopia() {
      return this.track.scrollWidth / 3;
    }

    vaiAllaCopiaCentrale() {
      var copia = this.larghezzaCopia();
      if (copia <= 0) return;
      this.posizione = copia;
      this.viewport.scrollLeft = copia;
    }

    /** Riporta la posizione dentro la copia centrale, senza che si veda. */
    normalizza() {
      var copia = this.larghezzaCopia();
      if (copia <= 0) return;
      if (performance.now() < this.dolceFinoA) return;

      var x = this.viewport.scrollLeft;
      if (x >= copia * 2) {
        x -= copia;
      } else if (x < copia * 0.5) {
        x += copia;
      } else {
        return;
      }
      this.viewport.scrollLeft = x;
      this.posizione = x;
    }

    suScorrimento() {
      if (this.trascinando) return;
      if (this.motiviPausa.size === 0) return; // ci pensa il ciclo di animazione
      this.normalizza();
    }

    pausa(motivo) {
      this.motiviPausa.add(motivo);
      this.fermaFrame();
    }

    riprendi(motivo) {
      this.motiviPausa.delete(motivo);
      this.avvia();
    }

    /** Pausa breve dopo un gesto, cosi il nastro non "riparte in faccia". */
    pausaBreve() {
      this.pausa('gesto');
      clearTimeout(this.timerGesto);
      this.timerGesto = setTimeout(() => this.riprendi('gesto'), PAUSA_DOPO_GESTO_MS);
    }

    avvia() {
      if (!this.autoplay) return;
      if (this.motiviPausa.size > 0) return;
      if (this.frame !== null) return;
      // Il primo frame dopo una pausa non muove niente: rimette in moto
      // l'orologio, e per questo la ripresa non produce scatti.
      this.ultimoTempo = null;
      this.frame = requestAnimationFrame((t) => this.passo(t));
    }

    fermaFrame() {
      if (this.frame !== null) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }
      this.ultimoTempo = null;
    }

    passo(t) {
      this.frame = requestAnimationFrame((x) => this.passo(x));

      if (this.ultimoTempo === null) {
        this.ultimoTempo = t;
        return;
      }

      var dt = (t - this.ultimoTempo) / 1000;
      this.ultimoTempo = t;
      if (dt > 0.1) dt = 0.1; // scheda tornata in primo piano: niente balzi

      var copia = this.larghezzaCopia();
      if (copia <= 0) return;

      // Se qualcuno ha mosso il nastro (rotellina, dito) ci si riallinea a lui
      // invece di riportarlo indietro. La tolleranza copre l'arrotondamento a
      // pixel interi che alcuni browser fanno su scrollLeft.
      var reale = this.viewport.scrollLeft;
      if (Math.abs(reale - this.posizione) > 2) this.posizione = reale;

      this.posizione += (copia / this.durataGiro) * dt;
      if (this.posizione >= copia * 2) this.posizione -= copia;
      this.viewport.scrollLeft = this.posizione;
    }

    inizioTrascinamento(e) {
      if (e.pointerType !== 'mouse') return; // col dito scorre gia il browser
      if (e.button !== 0) return;
      this.trascinando = true;
      this.spostamento = 0;
      // Se il trascinamento precedente non ha prodotto nessun clic da
      // sopprimere (il puntatore si e alzato fuori da un logo) il flag resta
      // alzato: azzerarlo qui evita che si mangi il primo clic buono dopo.
      this.sopprimiClic = false;
      this.xIniziale = e.clientX;
      this.scrollIniziale = this.viewport.scrollLeft;
      this.setAttribute('data-dragging', '');
      this.pausa('trascinamento');

      // Niente `setPointerCapture`: catturare il puntatore sposta il bersaglio
      // del clic sul contenitore, e cliccare un logo non aprirebbe piu il
      // brand — misurato, nessun clic arrivava ai link. Per seguire il mouse
      // anche fuori dal nastro bastano gli ascoltatori su window, che vengono
      // tolti appena si rilascia.
      this.inAscolto = {
        move: (ev) => this.duranteTrascinamento(ev),
        up: (ev) => this.fineTrascinamento(ev)
      };
      window.addEventListener('pointermove', this.inAscolto.move);
      window.addEventListener('pointerup', this.inAscolto.up);
      window.addEventListener('pointercancel', this.inAscolto.up);
    }

    smettiDiSeguireIlMouse() {
      if (!this.inAscolto) return;
      window.removeEventListener('pointermove', this.inAscolto.move);
      window.removeEventListener('pointerup', this.inAscolto.up);
      window.removeEventListener('pointercancel', this.inAscolto.up);
      this.inAscolto = null;
    }

    duranteTrascinamento(e) {
      if (!this.trascinando) return;
      var dx = e.clientX - this.xIniziale;
      if (Math.abs(dx) > this.spostamento) this.spostamento = Math.abs(dx);
      this.viewport.scrollLeft = this.scrollIniziale - dx;
      this.normalizza();
      e.preventDefault();
    }

    fineTrascinamento(e) {
      if (!this.trascinando) return;
      this.trascinando = false;
      this.removeAttribute('data-dragging');
      this.smettiDiSeguireIlMouse();
      // Trascinare sopra un logo non deve aprire quel brand.
      this.sopprimiClic = this.spostamento > SOGLIA_TRASCINAMENTO_PX;
      this.riprendi('trascinamento');
      this.pausaBreve();
    }

    filtraClic(e) {
      if (!this.sopprimiClic) return;
      this.sopprimiClic = false;
      e.preventDefault();
      e.stopPropagation();
    }

    daTastiera(e) {
      var cella = this.track.querySelector('.th-brands__item');
      var passo = cella ? cella.getBoundingClientRect().width + 16 : 120;
      var salto = 0;

      if (e.key === 'ArrowRight') salto = passo;
      else if (e.key === 'ArrowLeft') salto = -passo;
      else if (e.key === 'PageDown') salto = this.viewport.clientWidth * 0.9;
      else if (e.key === 'PageUp') salto = this.viewport.clientWidth * -0.9;
      else return;

      e.preventDefault();
      this.pausaBreve();
      this.dolceFinoA = performance.now() + DURATA_SCORRIMENTO_DOLCE_MS;
      this.viewport.scrollBy({ left: salto, behavior: 'smooth' });
    }
  }

  customElements.define('th-brand-rail', ThBrandRail);
})();
